import { NavLink } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

const FOOTER_INFO_HIDDEN = new Set(['about', 'information/technical-information', 'information/notice-and-takedown']);

const MANAGE_COOKIES_LABEL = {
  en: 'Manage cookies',
  cy: 'Rheoli cwcis',
  gd: 'Stiùirich criomagan',
};

export default function FooterMenu({ onManageCookies }) {
  const lang = useLanguage();

  return (
    <nav aria-label={lang.ui.footerNav} className="bg-white dark:bg-dark-950 border-t border-blue-200 dark:border-dark-700 py-2">
      <div className="max-w-7xl mx-auto px-6">
        {/* text-sm (14 px): 12 px footer text was hard to read for low-vision users (N22). */}
        {/* Footer links and "Manage cookies" are underlined: blue text alone (1.7–2.2:1 against
            the surrounding text) does not identify them as links (WCAG 1.4.1 Use of Color). */}
        {/* The 9rem indent lines the menu up with the logo on wide screens only;
            on narrow ones it squeezed each column to ~56 px and pushed the page
            wider than a 320 px viewport (WCAG 1.4.10 Reflow). */}
        <div className="footer-menu md:pl-36">
          <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-4 [overflow-wrap:anywhere]">

            {/* Column 1: Main navigation */}
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-blue-800 dark:text-dark-100 mb-1 pb-0.5 border-b border-blue-200 dark:border-dark-700">
                {lang.siteTitle}
              </h2>
              <ul className="space-y-0.5">
                {lang.menu.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.slug === ''}
                      className={({ isActive }) =>
                        `text-sm underline underline-offset-2 hover:decoration-2 transition-colors ${
                          isActive
                            ? 'text-blue-800 dark:text-dark-100 font-semibold'
                            : 'text-blue-700 dark:text-dark-400 hover:text-blue-900 dark:hover:text-dark-100'
                        }`
                      }
                    >
                      {item.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: Information section */}
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-blue-800 dark:text-dark-100 mb-1 pb-0.5 border-b border-blue-200 dark:border-dark-700">
                {lang.information.label}
              </h2>
              <ul className="space-y-0.5">
                {lang.information.pages
                  .filter((page) => !FOOTER_INFO_HIDDEN.has(page.slug))
                  .map((page) => (
                    <li key={page.path}>
                      {page.isExternal ? (
                        <a
                          href={page.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm underline underline-offset-2 hover:decoration-2 transition-colors text-blue-700 dark:text-dark-400 hover:text-blue-900 dark:hover:text-dark-100"
                        >
                          {page.name}
                          {/* G201: warn that the link opens a new tab. */}
                          <span className="sr-only"> {lang.ui.newTab}</span>
                        </a>
                      ) : (
                        <NavLink
                          to={page.path}
                          className={({ isActive }) =>
                            `text-sm underline underline-offset-2 hover:decoration-2 transition-colors ${
                              isActive
                                ? 'text-blue-800 dark:text-dark-100 font-semibold'
                                : 'text-blue-700 dark:text-dark-400 hover:text-blue-900 dark:hover:text-dark-100'
                            }`
                          }
                        >
                          {page.name}
                        </NavLink>
                      )}
                    </li>
                  ))}
                {onManageCookies && (
                  <li>
                    {/* A link like its siblings, so link navigation (TalkBack "Links",
                        NVDA/JAWS K, VoiceOver rotor) reaches it instead of announcing
                        "No next link" after Sitemap (bug #58). */}
                    <a
                      href="#cookie-settings"
                      onClick={(e) => { e.preventDefault(); onManageCookies(); }}
                      className="text-sm transition-colors text-blue-700 dark:text-dark-400 hover:text-blue-900 dark:hover:text-dark-100 underline underline-offset-2 hover:decoration-2"
                    >
                      {MANAGE_COOKIES_LABEL[lang.code] || MANAGE_COOKIES_LABEL.en}
                    </a>
                  </li>
                )}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
}
